import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import TuneIcon from '@mui/icons-material/Tune';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAPI } from '../Hooks/useAPIs';
import { useDebounce } from '../Hooks/useDebounce';
import {
  FILTER_PREFERENCE_PAGES,
  FILTER_PREFERENCE_STORAGE_KEY,
  encodeSavedSignalEntries,
  readStoredSignalEntries,
  getDefaultFilterPreferenceConfig,
  getFilterPreferencePageById,
  readFilterPreferenceConfig,
  readStoredFilters,
  writeFilterPreferenceConfig,
  writeStoredFilters,
  writeStoredSignalEntries,
} from './filterPreferencesConfig';
import FilterDropdown from '../common/FilterDropdown/FilterDropdown';
import { useUserPreferences } from '../Hooks/useUserPreferences';
import { useSnackbarNotification } from '../Hooks/useSnackbarNotification';

import './FilterPreferencesPage.css';

const PreferenceSection = ({ title, children, rightAction = null }) => (
  <Paper elevation={0} className="filter-preferences-paper">
    <Box className="filter-preferences-paper-header">
      {typeof title === 'string' ? (
        <Typography className="filter-preferences-paper-title">{title}</Typography>
      ) : (
        title
      )}
      {rightAction}
    </Box>
    <Box className="filter-preferences-paper-body">{children}</Box>
  </Paper>
);

const FilterPreferencesPage = ({ activeTheme }) => {
  const { savePreferencesToApi, isSaving } = useUserPreferences();
  const { apiCall } = useAPI();

  const [selectedPageId, setSelectedPageId] = useState(FILTER_PREFERENCE_PAGES[0]?.id || 'files');
  const [draftConfig, setDraftConfig] = useState(() => readFilterPreferenceConfig());
  const [draftValuesByPage, setDraftValuesByPage] = useState({});
  const [signalSearchTerm, setSignalSearchTerm] = useState('');
  const debouncedSignalSearch = useDebounce(signalSearchTerm, 300);
  const [storageVersion, setStorageVersion] = useState(0);
  const { showSnackbar } = useSnackbarNotification();

  useEffect(() => {
    const handleStorageRefresh = () => {
      setDraftConfig(readFilterPreferenceConfig());
      setDraftValuesByPage({});
      setStorageVersion((c) => c + 1);
    };
    window.addEventListener('storage', handleStorageRefresh);
    window.addEventListener('localStorageChange', handleStorageRefresh);
    return () => {
      window.removeEventListener('storage', handleStorageRefresh);
      window.removeEventListener('localStorageChange', handleStorageRefresh);
    };
  }, []);

  const selectedPage = useMemo(
    () => getFilterPreferencePageById(selectedPageId) || FILTER_PREFERENCE_PAGES[0],
    [selectedPageId],
  );
  const isSignalListPage = selectedPage?.storageMode === 'signal-list';

  const pagePreference =
    draftConfig[selectedPage?.id] || getDefaultFilterPreferenceConfig()[selectedPage?.id];
  const selectedPageFields = useMemo(
    () => selectedPage?.fields.map((f) => f.key) || [],
    [selectedPage],
  );

  const savedSignalEntries = useMemo(() => {
    if (!selectedPage || !isSignalListPage) return [];
    return readStoredSignalEntries(selectedPage.storageKey);
  }, [isSignalListPage, selectedPage, storageVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const savedValuesForSelectedPage = useMemo(() => {
    if (!selectedPage || isSignalListPage) return {};
    return readStoredFilters(selectedPage.storageKey, selectedPageFields);
  }, [isSignalListPage, selectedPage, selectedPageFields, storageVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const draftValues =
    draftValuesByPage[selectedPage?.id] ||
    (isSignalListPage ? savedSignalEntries : savedValuesForSelectedPage);

  const savedFieldSummary = useMemo(() => {
    if (!selectedPage || isSignalListPage) return [];
    return Object.entries(savedValuesForSelectedPage).filter(
      ([, v]) => Array.isArray(v) && v.length > 0,
    );
  }, [isSignalListPage, savedValuesForSelectedPage, selectedPage]);

  const updateSelectedPageConfig = useCallback(
    (updates) => {
      if (!selectedPage) return;
      setDraftConfig((c) => ({
        ...c,
        [selectedPage.id]: { ...c[selectedPage.id], ...updates },
      }));
    },
    [selectedPage],
  );

  const updateDraftFieldValue = useCallback(
    (fieldKey, nextValue) => {
      if (!selectedPage) return;
      setDraftValuesByPage((c) => ({
        ...c,
        [selectedPage.id]: {
          ...savedValuesForSelectedPage,
          ...c[selectedPage.id],
          [fieldKey]: nextValue,
        },
      }));
    },
    [selectedPage, savedValuesForSelectedPage],
  );

  const SIGNAL_PAGE_SIZE = 100;
  const signalAutocompleteQuery = useInfiniteQuery({
    queryKey: ['pref-signal-autocomplete', selectedPage?.signalAutocompleteEndpoint, debouncedSignalSearch],
    queryFn: async ({ pageParam = 0 }) => {
      const params = new URLSearchParams({
        limit: String(SIGNAL_PAGE_SIZE),
        offset: String(pageParam),
      });
      if (debouncedSignalSearch.trim()) params.append('q', debouncedSignalSearch.trim());
      const res = await apiCall('GET', `${selectedPage.signalAutocompleteEndpoint}?${params}`);
      const data = res?.data;
      const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      const hasMore =
        typeof data?.has_more === 'boolean' ? data.has_more : results.length >= SIGNAL_PAGE_SIZE;
      return { results, hasMore, nextOffset: pageParam + SIGNAL_PAGE_SIZE };
    },
    getNextPageParam: (lastPage) => (lastPage?.hasMore ? lastPage.nextOffset : undefined),
    initialPageParam: 0,
    enabled: isSignalListPage && !!selectedPage?.signalAutocompleteEndpoint,
    keepPreviousData: true,
  });

  const signalOptions = useMemo(() => {
    const pages = signalAutocompleteQuery.data?.pages || [];
    const merged = pages.flatMap((page) => page.results || []);
    const seen = new Set();
    return merged.filter((item) => {
      if (!item?.signal) return false;
      if (seen.has(item.signal)) return false;
      seen.add(item.signal);
      return true;
    });
  }, [signalAutocompleteQuery.data]);

  const handleSignalListboxScroll = useCallback(
    (event) => {
      const node = event.currentTarget;
      if (node.scrollHeight - node.scrollTop - node.clientHeight <= 40) {
        if (signalAutocompleteQuery.hasNextPage && !signalAutocompleteQuery.isFetchingNextPage) {
          signalAutocompleteQuery.fetchNextPage();
        }
      }
    },
    [signalAutocompleteQuery],
  );

  const handleAddSignalEntry = useCallback((option) => {
    if (!selectedPage || !isSignalListPage) return;
    const signalName = (typeof option === 'string' ? option : option?.signal || '').trim();
    const network = (typeof option === 'string' ? '' : option?.network || '').trim();
    if (!signalName) return;
    const nextEntries = Array.isArray(draftValues) ? [...draftValues] : [];
    if (nextEntries.some((e) => e.signalName === signalName && e.network === network)) return;
    setDraftValuesByPage((c) => ({
      ...c,
      [selectedPage.id]: [...nextEntries, { signalName, network }],
    }));
  }, [selectedPage, isSignalListPage, draftValues]);

  const handleRemoveSignalEntry = useCallback(
    (entryToRemove) => {
      if (!selectedPage || !isSignalListPage) return;
      const nextEntries = (Array.isArray(draftValues) ? draftValues : []).filter(
        (e) =>
          !(e.signalName === entryToRemove.signalName && e.network === entryToRemove.network),
      );
      setDraftValuesByPage((c) => ({ ...c, [selectedPage.id]: nextEntries }));
    },
    [selectedPage, isSignalListPage, draftValues],
  );

  const handleSavePreferences = useCallback(async () => {
    if (!selectedPage) return;
    const normalizedConfig = writeFilterPreferenceConfig(draftConfig);
    const pageConfig = normalizedConfig[selectedPage.id];
    let storedValues = null;

    if (isSignalListPage) {
      const entries = pageConfig.enabled
        ? Array.isArray(draftValues)
          ? draftValues
          : []
        : [];
      writeStoredSignalEntries(selectedPage.storageKey, entries);
      storedValues = encodeSavedSignalEntries(entries);
    } else {
      const fieldsToStore = pageConfig.enabled ? pageConfig.selectedFields : [];
      const valuesToStore = selectedPage.fields.reduce((acc, field) => {
        acc[field.key] = draftValues[field.key] || [];
        return acc;
      }, {});
      writeStoredFilters(selectedPage.storageKey, valuesToStore, fieldsToStore);
      storedValues = valuesToStore;
    }

    await savePreferencesToApi({
      [FILTER_PREFERENCE_STORAGE_KEY]: normalizedConfig,
      [selectedPage.storageKey]: storedValues,
    });

    setDraftConfig(normalizedConfig);
    setDraftValuesByPage((c) => ({
      ...c,
      [selectedPage.id]: isSignalListPage
        ? readStoredSignalEntries(selectedPage.storageKey)
        : readStoredFilters(selectedPage.storageKey, selectedPageFields),
    }));
    setStorageVersion((c) => c + 1);
    showSnackbar({ message: 'Preferences saved!', severity: 'success' });
  }, [
    selectedPage,
    draftConfig,
    isSignalListPage,
    draftValues,
    selectedPageFields,
    savePreferencesToApi,
    showSnackbar,
  ]);

  const handleResetSelectedPage = useCallback(() => {
    if (!selectedPage) return;
    setDraftConfig((c) => ({
      ...c,
      [selectedPage.id]: getDefaultFilterPreferenceConfig()[selectedPage.id],
    }));
    setDraftValuesByPage((c) => ({
      ...c,
      [selectedPage.id]: isSignalListPage
        ? readStoredSignalEntries(selectedPage.storageKey)
        : readStoredFilters(selectedPage.storageKey, selectedPageFields),
    }));
    setSignalSearchTerm('');
  }, [selectedPage, isSignalListPage, selectedPageFields]);

  const handleClearSavedFilters = useCallback(() => {
    if (!selectedPage) return;
    if (isSignalListPage) {
      writeStoredSignalEntries(selectedPage.storageKey, []);
    } else {
      writeStoredFilters(selectedPage.storageKey, {}, []);
    }
    setDraftValuesByPage((c) => ({
      ...c,
      [selectedPage.id]: isSignalListPage
        ? []
        : readStoredFilters(selectedPage.storageKey, selectedPageFields),
    }));
    setStorageVersion((c) => c + 1);
    setSignalSearchTerm('');
    showSnackbar({
      message: `Cleared saved filters for ${selectedPage.title}.`,
      severity: 'info',
    });
  }, [selectedPage, isSignalListPage, selectedPageFields, showSnackbar]);

  if (!selectedPage || !pagePreference) return null;

  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="filter-preferences-container">
        {/* ── Page header ── */}
        <Box className="filter-preferences-header">
          <Box className="filter-preferences-title-container">
            <TuneIcon className="filter-preferences-title-icon" />
            <Typography variant="h5" className="filter-preferences-title">
              Filter Preferences
            </Typography>
          </Box>
          <Typography variant="body2" className="filter-preferences-subtitle">
            Configure which filter values are remembered on each page. Values sync on every login.
          </Typography>
        </Box>

        {/* ── Main grid ── */}
        <Box className="filter-preferences-main">
        <Box className="filter-preferences-grid">
          {/* ── Left: page tabs ── */}
          <Paper elevation={0} className="filter-preferences-sidebar">
            <Box className="filter-preferences-sidebar-header">
              <Typography className="filter-preferences-sidebar-title">Pages</Typography>
            </Box>
            <Tabs
              orientation="vertical"
              value={selectedPageId}
              onChange={(_, val) => setSelectedPageId(val)}
              className="filter-preferences-tabs"
              TabIndicatorProps={{ className: 'filter-preferences-tab-indicator' }}
            >
              {FILTER_PREFERENCE_PAGES.map((page) => {
                const conf = draftConfig[page.id];
                const isEnabled = conf?.enabled;
                return (
                  <Tab
                    key={page.id}
                    value={page.id}
                    className="filter-preferences-tab"
                    label={
                      <Box className="filter-preferences-tab-content">
                        <Box
                          className={`filter-preferences-tab-dot ${
                            isEnabled
                              ? 'filter-preferences-tab-dot-enabled'
                              : 'filter-preferences-tab-dot-disabled'
                          }`}
                        />
                        <Typography
                          className={`filter-preferences-tab-text ${
                            selectedPageId === page.id ? 'filter-preferences-tab-text-active' : ''
                          }`}
                        >
                          {page.title}
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
            </Tabs>
          </Paper>

          {/* ── Right: config + summary ── */}
          <Box className="filter-preferences-content-scroll">
            <Fade in key={selectedPage.id} timeout={200}>
              <div>
            <PreferenceSection
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography className="filter-preferences-paper-title">
                    {selectedPage.title}
                  </Typography>
                  <Chip
                    label={selectedPage.route}
                    size="small"
                    variant="outlined"
                    className="filter-preferences-route-chip"
                  />
                </Box>
              }
              rightAction={
                <Box
                  className={`filter-preferences-toggle-pill ${pagePreference.enabled ? 'enabled' : ''}`}
                  onClick={() => updateSelectedPageConfig({ enabled: !pagePreference.enabled })}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography
                    className="filter-preferences-toggle-pill-label"
                    sx={{ color: pagePreference.enabled ? '#2e7d32' : '#9e9e9e' }}
                  >
                    {pagePreference.enabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                  {pagePreference.enabled ? (
                    <ToggleOnIcon sx={{ fontSize: 28, color: '#4caf50' }} />
                  ) : (
                    <ToggleOffIcon sx={{ fontSize: 28, color: '#bdbdbd' }} />
                  )}
                </Box>
              }
            >
              <Stack spacing={2.5}>
                {/* ── Fields to remember ── */}
                {!isSignalListPage && selectedPage.fields.length > 0 && (
                  <Box className={!pagePreference.enabled ? 'filter-preferences-disabled-overlay' : ''}>
                    <Typography className="filter-preferences-section-title">
                      Fields to remember
                    </Typography>
                    <Box className="filter-preferences-fields-container">
                      {selectedPage.fields.map((field) => {
                        const checked = pagePreference.selectedFields.includes(field.key);
                        return (
                          <Chip
                            key={field.key}
                            label={field.label}
                            size="small"
                            variant={checked ? 'filled' : 'outlined'}
                            color={checked ? 'primary' : 'default'}
                            disabled={!pagePreference.enabled}
                            onClick={() => {
                              const next = checked
                                ? pagePreference.selectedFields.filter((f) => f !== field.key)
                                : [...pagePreference.selectedFields, field.key];
                              updateSelectedPageConfig({ selectedFields: next });
                            }}
                            className={
                              pagePreference.enabled
                                ? 'filter-preferences-chip-clickable'
                                : 'filter-preferences-chip-default'
                            }
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* ── Default values ── */}
                <Box className={!pagePreference.enabled ? 'filter-preferences-disabled-overlay' : ''}>
                  <Typography className="filter-preferences-section-title">
                    {isSignalListPage ? 'Default Signals' : 'Default Values'}
                  </Typography>
                  {isSignalListPage ? (
                    <Stack spacing={1.5}>
                      {selectedPage.signalAutocompleteEndpoint ? (
                        <Autocomplete
                          fullWidth
                          size="small"
                          disableCloseOnSelect
                          options={signalOptions}
                          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.signal)}
                          filterOptions={(x) => x}
                          value={null}
                          inputValue={signalSearchTerm}
                          onInputChange={(_, val, reason) => {
                            if (reason === 'reset') setSignalSearchTerm('');
                            else setSignalSearchTerm(val);
                          }}
                          onChange={(_, option) => {
                            if (option) {
                              handleAddSignalEntry(option);
                              setSignalSearchTerm('');
                            }
                          }}
                          disabled={!pagePreference.enabled}
                          loading={signalAutocompleteQuery.isFetching}
                          ListboxProps={{
                            onScroll: handleSignalListboxScroll,
                            style: { maxHeight: 300 },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Add Signal"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <AutoGraphIcon className="filter-preferences-autocomplete-input-start" />
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                                endAdornment: (
                                  <>
                                    {signalAutocompleteQuery.isFetching && (
                                      <CircularProgress color="inherit" size={16} />
                                    )}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => {
                            const alreadyAdded = (Array.isArray(draftValues) ? draftValues : []).some(
                              (e) => e.signalName === option.signal && e.network === (option.network || ''),
                            );
                            const meta = [
                              option.longname,
                              option.network ? `Network: ${option.network}` : '',
                            ].filter(Boolean);
                            return (
                              <li {...props} key={`${option.signal}|${option.network || ''}`}>
                                <Box className="filter-preferences-signal-item">
                                  <Box className="filter-preferences-signal-item-header">
                                    <GraphicEqIcon className="filter-preferences-signal-item-icon" />
                                    <Typography variant="body2">{option.signal}</Typography>
                                    {alreadyAdded && (
                                      <CheckCircleIcon className="filter-preferences-signal-item-check" />
                                    )}
                                  </Box>
                                  {meta.length > 0 && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      className="filter-preferences-signal-item-meta"
                                    >
                                      {meta.join(' • ')}
                                    </Typography>
                                  )}
                                </Box>
                              </li>
                            );
                          }}
                          noOptionsText={
                            signalAutocompleteQuery.isFetching ? 'Loading...' : 'No signals found'
                          }
                        />
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          disabled={!pagePreference.enabled}
                          label="Signal Name"
                          helperText="Type a name and press Enter"
                          value={signalSearchTerm}
                          onChange={(e) => setSignalSearchTerm(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && signalSearchTerm.trim()) {
                              handleAddSignalEntry(signalSearchTerm.trim());
                              setSignalSearchTerm('');
                            }
                          }}
                        />
                      )}
                      <Box className="filter-preferences-signals-container">
                        {Array.isArray(draftValues) && draftValues.length > 0 ? (
                          draftValues.map((entry) => (
                            <Chip
                              key={`${entry.signalName}|${entry.network}`}
                              label={
                                entry.network
                                  ? `${entry.signalName} (${entry.network})`
                                  : entry.signalName
                              }
                              onDelete={
                                pagePreference.enabled
                                  ? () => handleRemoveSignalEntry(entry)
                                  : undefined
                              }
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ py: 2 }}>
                            No signals added yet. Search above to get started.
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  ) : (
                    <Stack spacing={1.5}>
                      {selectedPage.fields
                        .filter((field) => pagePreference.selectedFields.includes(field.key))
                        .map((field) => {
                          const otherSingleSelectKey = field.singleSelect
                            ? selectedPage.fields.find(
                                (f) => f.singleSelect && f.key !== field.key,
                              )?.key
                            : null;
                          const isExclusivelyDisabled = otherSingleSelectKey
                            ? (draftValues[otherSingleSelectKey] || []).length > 0
                            : false;

                          return (
                            <FilterDropdown
                              key={field.key}
                              field={field.key}
                              label={field.label}
                              value={
                                field.singleSelect
                                  ? (draftValues[field.key] || [])[0] ?? null
                                  : draftValues[field.key] || []
                              }
                              onChange={(val) =>
                                updateDraftFieldValue(
                                  field.key,
                                  field.singleSelect ? (val ? [val] : []) : val,
                                )
                              }
                              apiBaseUrl={selectedPage.apiBaseUrl || '/api/filters'}
                              multiple={!field.singleSelect}
                              disabled={!pagePreference.enabled || isExclusivelyDisabled}
                            />
                          );
                        })}
                      {pagePreference.selectedFields.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                          Toggle at least one field above to set default values.
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Box>

                {/* ── Actions ── */}
                <Divider />
                <Box className="filter-preferences-actions">
                  <Tooltip title="Save preferences for this page" arrow>
                    <span>
                      <Button
                        variant="contained"
                        startIcon={
                          isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                        }
                        onClick={handleSavePreferences}
                        disabled={isSaving}
                      >
                        {isSaving ? 'Saving…' : 'Save Preferences'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Reset to last saved state" arrow>
                    <Button
                      variant="outlined"
                      startIcon={<RestartAltIcon />}
                      onClick={handleResetSelectedPage}
                    >
                      Reset
                    </Button>
                  </Tooltip>
                  <Tooltip title="Remove all saved filter values for this page" arrow>
                    <Button
                      variant="text"
                      color="error"
                      startIcon={<DeleteSweepIcon />}
                      onClick={handleClearSavedFilters}
                    >
                      Clear Saved
                    </Button>
                  </Tooltip>
                </Box>

                {/* ── Currently saved values (inline) ── */}
                {(savedFieldSummary.length > 0 ||
                  (isSignalListPage && savedSignalEntries.length > 0)) && (
                  <>
                    <Divider />
                    <Box className="filter-preferences-saved-section">
                      <Typography className="filter-preferences-section-title">
                        Currently saved
                      </Typography>
                      {isSignalListPage ? (
                        <Box className="filter-preferences-fields-container">
                          {savedSignalEntries.map((e) => (
                            <Chip
                              key={`${e.signalName}|${e.network}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              label={e.network ? `${e.signalName} (${e.network})` : e.signalName}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Stack spacing={0.5}>
                          {savedFieldSummary.map(([fieldKey, value]) => {
                            const label =
                              selectedPage.fields.find((f) => f.key === fieldKey)?.label || fieldKey;
                            return (
                              <Box key={fieldKey} className="filter-preferences-saved-row">
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  className="filter-preferences-saved-label"
                                >
                                  {label}:
                                </Typography>
                                {value.map((v) => (
                                  <Chip key={v} label={v} size="small" variant="outlined" />
                                ))}
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  </>
                )}
              </Stack>
            </PreferenceSection>
              </div>
            </Fade>
          </Box>
        </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default FilterPreferencesPage;
